import type { ApiErrorResponse } from '@/lib/apiClient';
import { apiClient } from '@/lib/apiClient';
import type { DateString, Pagination } from '@/types/api';
import type { NotificationCategoryType, NotificationItem } from '@/types/notification';
import type { InfiniteData, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';

export interface GetNotificationsResponse {
  notifications: NotificationItem[];
  pagination: Pagination;
  unreadCount: number;
}

interface GetNotificationsQueryParams {
  limit?: number;
  page?: number;
  categoryType?: NotificationCategoryType;
  since?: DateString;
}

export const getNotifications = async (params: GetNotificationsQueryParams) => {
  const response = await apiClient.get<GetNotificationsResponse>('/v1/notifications', { params });
  return response.data;
};

interface UseGetNotificationsInfiniteOptions extends Omit<GetNotificationsQueryParams, 'page'> {
  options?: Omit<
    UseInfiniteQueryOptions<
      GetNotificationsResponse,
      ApiErrorResponse,
      InfiniteData<GetNotificationsResponse, number>,
      GetNotificationsResponse,
      readonly unknown[],
      number
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >;
}

export const GET_NOTIFICATIONS_INFINITE_QUERY_KEY = 'getNotificationsInfinite';

export const useGetNotificationsInfinite = ({ options, ...queryParams }: UseGetNotificationsInfiniteOptions = {}) => {
  return useInfiniteQuery({
    queryKey: [GET_NOTIFICATIONS_INFINITE_QUERY_KEY, queryParams],
    queryFn: ({ pageParam = 1 }) => getNotifications({ ...queryParams, page: pageParam }),
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      const hasNextPage = pagination.page < pagination.totalPages;
      return hasNextPage ? pagination.page + 1 : undefined;
    },
    initialPageParam: 1,
    ...options,
  });
};
